<template>
  <div class="mt-5">
    <v-dialog
      :value="dialog"
      width="1500px"
      persistent
      scrollable>
      <v-card class="upgrade-dialog">
        <v-dialog v-model="dialogSuccess" max-width="420" persistent>
          <v-card class="success-dialog">
            <v-card-text class="text-center py-8 px-6">
              <v-avatar size="64" color="#e8f5e9" class="mb-4">
                <v-icon color="#2e7d32" size="34">mdi-check-circle</v-icon>
              </v-avatar>
              <div class="title font-weight-bold mb-2">Berhasil</div>
              <div class="body-2 grey--text text--darken-1">
                {{ successMessage || 'Upgrade berhasil disimpan.' }}
              </div>
            </v-card-text>
            <v-card-actions class="px-6 pb-5">
              <v-spacer></v-spacer>
              <v-btn color="primary" class="text-capitalize px-6" elevation="0" @click="closeSuccessDialog">
                Oke
              </v-btn>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-card-title class="upgrade-dialog__title">
          <div>
            <div class="title font-weight-bold">Upgrade Data Pemakaian / Penggunaan</div>
            <div class="caption grey--text text--darken-1 mt-1">Buat data upgrade baru tanpa mengubah order sebelumnya.</div>
          </div>
        </v-card-title>
        <v-divider class="mb-2" style="border-top: 2px dashed #e0e0e0;"/>
        <v-card-text class="upgrade-dialog__body">
          <ValidationObserver ref="form">
            <v-form>
              <v-row>
                <v-col cols="12" md="4">
                  <div class="upgrade-sidebar">
                    <div class="upgrade-sidebar__header">
                      <div class="upgrade-section-kicker">Ringkasan Order Lama</div>
                      <div class="title font-weight-bold mt-1">Data Sebelumnya</div>
                      <div class="caption grey--text text--darken-1 mt-1">Gunakan ringkasan ini untuk membandingkan order lama dengan upgrade yang akan dibuat.</div>
                    </div>

                    <div class="upgrade-overview-card">
                      <div class="upgrade-overview-card__label">Total Order Lama</div>
                      <div class="upgrade-overview-card__value">{{ oldOrderTotal | price }}</div>
                      <div class="upgrade-overview-card__subtext">
                        {{ order.product_name || '-' }}
                      </div>
                    </div>

                    <div class="upgrade-summary-grid">
                      <div class="upgrade-summary-cell upgrade-summary-cell--wide">
                        <div class="upgrade-summary-label">Nama Pelanggan</div>
                        <div class="upgrade-summary-value">{{ order.customer_name || '-' }}</div>
                      </div>

                      <div class="upgrade-summary-cell upgrade-summary-cell--wide">
                        <div class="upgrade-summary-label">Tanggal Booking</div>
                        <div class="upgrade-summary-value">
                          <span v-if="order.event_start_date === order.event_end_date">
                            {{ order.event_start_date | dateFull }}
                          </span>
                          <span v-else>
                            {{ order.event_start_date | dateFull }} - {{ order.event_end_date | dateFull }}
                          </span>
                        </div>
                      </div>

                      <div class="upgrade-summary-cell">
                        <div class="upgrade-summary-label">Nama Event</div>
                        <div class="upgrade-summary-value">{{ order.so_event_name || '-' }}</div>
                      </div>

                      <div class="upgrade-summary-cell">
                        <div class="upgrade-summary-label">Jenis</div>
                        <div class="upgrade-summary-value">{{ order.event_name || '-' }}</div>
                      </div>
                    </div>

                    <div class="upgrade-card upgrade-card--plain">
                      <div class="subtitle-2 font-weight-bold mb-3">Detail Nominal Lama</div>
                      <div v-if="oldPriceLines.length > 0">
                        <div
                          v-for="(item, index) in oldPriceLines"
                          :key="index"
                          :class="index > 0 ? 'mt-3 pt-3' : ''"
                          :style="index > 0 ? 'border-top: 1px dashed #e0e0e0;' : ''">
                          <div class="d-flex justify-space-between align-center">
                            <div class="body-2 black--text">{{ item.date | date }}</div>
                            <div class="body-2 black--text">{{ getLinePrice(item) | price }}</div>
                          </div>
                          <div
                            v-if="item.fasilitas_array && item.fasilitas_array.length > 0"
                            class="mt-2 pl-2"
                            style="border-left: 3px solid #e0e0e0;">
                            <div v-for="(facility, facilityIndex) in item.fasilitas_array" :key="facilityIndex" class="mb-1">
                              <div class="d-flex justify-space-between align-center">
                                <div class="caption" style="color: #666;">
                                  • {{ facility.product_facility_name }}
                                  <span class="ml-1" style="color: #999;">({{ facility.qty }}x)</span>
                                </div>
                                <div class="caption" style="color: #666;">
                                  {{ getFacilitySubtotal(facility) | price }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-else class="body-2 grey--text text--darken-1">Belum ada detail nominal.</div>

                      <v-divider class="my-3"></v-divider>

                      <div class="d-flex justify-space-between align-center">
                        <div class="subtitle-2 font-weight-bold">Total Lama</div>
                        <div class="subtitle-2 font-weight-bold">{{ oldOrderTotal | price }}</div>
                      </div>
                    </div>

                    <div class="upgrade-card upgrade-card--summary mt-4">
                      <div class="subtitle-2 font-weight-bold mb-3">Perhitungan Upgrade</div>
                      <div class="d-flex justify-space-between align-center mb-2">
                        <div class="body-2">Total Lama</div>
                        <div class="body-2 font-weight-medium">{{ oldOrderTotal | price }}</div>
                      </div>
                      <div class="d-flex justify-space-between align-center mb-2">
                        <div class="body-2">Total Baru</div>
                        <div class="body-2 font-weight-medium">
                          <span v-if="hasCalculatedNewTotal">{{ newOrderTotal | price }}</span>
                          <span v-else>-</span>
                        </div>
                      </div>
                      <v-divider class="my-2"></v-divider>
                      <div class="d-flex justify-space-between align-center">
                        <div class="subtitle-2 font-weight-bold">Total Bayar Tambahan</div>
                        <div class="subtitle-2 font-weight-bold primary--text">
                          <span v-if="hasCalculatedNewTotal">{{ totalBayarTambahan | price }}</span>
                          <span v-else>-</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>

                <v-col cols="12" md="8">
                  <v-row>
                    <v-col cols="12" md="5">
                      <div class="upgrade-pane">
                        <div class="upgrade-pane__header">
                          <div class="upgrade-section-kicker">Data Pemesan</div>
                          <div class="subtitle-1 font-weight-bold mt-1">Informasi Pemesan & Dokumen</div>
                          <div class="caption grey--text text--darken-1 mt-1">Perbarui data pemesan yang akan dipakai pada order upgrade.</div>
                        </div>
                      <ValidationProvider name="Nama Lengkap" rules="required" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Nama Lengkap
                        </div>
                        <v-text-field
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. John Doe"
                          hide-details
                          v-model="form.name"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.name !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-text-field>
                      </ValidationProvider>

                      <ValidationProvider name="Email" rules="required|email" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Email
                        </div>
                        <v-text-field
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. xxx@mail.com"
                          hide-details
                          v-model="form.email"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.email !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-text-field>
                      </ValidationProvider>

                      <ValidationProvider name="Nomor Handphone" rules="required|min:10" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Nomor Handphone
                        </div>
                        <v-text-field
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. 08123456789"
                          hide-details
                          v-model="form.mobilephone"
                          :error-messages="errors"
                          required
                          type="number"
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.mobilephone !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-text-field>
                      </ValidationProvider>

                      <ValidationProvider name="NIK" rules="required|min:16" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          NIK
                        </div>
                        <v-text-field
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. 1234567890123456"
                          hide-details
                          onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                          type="number"
                          v-model="form.nik"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.nik !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-text-field>
                      </ValidationProvider>

                      <div class="mt-2 pb-5">
                        <div class="d-flex align-center justify-space-between">
                          <div style="width: 47%;">
                            <div class="body-2 mb-2">
                              <span class="red--text">*</span>
                              Tanggal Mulai
                            </div>
                            <v-menu
                              v-model="menuStart"
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="startFormatted"
                                  placeholder="Pilih tanggal mulai"
                                  readonly
                                  outlined
                                  dense
                                  disabled
                                  hide-details
                                  v-bind="attrs"
                                  v-on="on"
                                  class="form-input"
                                  style="border-radius: 4px;" />
                              </template>
                              <v-date-picker
                                v-model="form.start_date"
                                :min="today"
                                :allowed-dates="allowedDates"
                                @input="menuStart = false"
                                color="primary" />
                            </v-menu>
                          </div>

                          <div class="px-2 pt-6">~</div>

                          <div style="width: 47%;">
                            <div class="body-2 mb-2">
                              <span class="red--text">*</span>
                              Tanggal Selesai
                            </div>
                            <v-menu
                              v-model="menuEnd"
                              :close-on-content-click="false"
                              :nudge-right="40"
                              transition="scale-transition"
                              offset-y
                              min-width="auto">
                              <template v-slot:activator="{ on, attrs }">
                                <v-text-field
                                  v-model="endFormatted"
                                  placeholder="Pilih tanggal selesai"
                                  readonly
                                  outlined
                                  dense
                                  disabled
                                  hide-details
                                  v-bind="attrs"
                                  v-on="on"
                                  class="form-input"
                                  style="border-radius: 4px;" />
                              </template>
                              <v-date-picker
                                v-model="form.end_date"
                                :min="form.start_date || today"
                                :allowed-dates="allowedDates"
                                @input="menuEnd = false"
                                color="primary" />
                            </v-menu>
                          </div>
                        </div>
                      </div>

                      <ValidationProvider name="Nama Event" rules="required" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Nama Event
                        </div>
                        <v-text-field
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. Event Name"
                          hide-details
                          v-model="form.event_name"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.event_name !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-text-field>
                      </ValidationProvider>

                      <ValidationProvider name="Deskripsi Event" rules="required" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Deskripsi Event
                        </div>
                        <v-textarea
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. Deskripsi Event"
                          hide-details
                          v-model="form.event_description"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.event_description !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-textarea>
                      </ValidationProvider>

                      <ValidationProvider name="Alamat" rules="required" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Alamat
                        </div>
                        <v-textarea
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. Alamat Lengkap"
                          hide-details
                          v-model="form.address"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="form.address !== '' && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-textarea>
                      </ValidationProvider>

                      <div class="mt-3">
                        <div class="form-group pb-4">
                          <div class="body-2 mb-2">
                            <span class="red--text">*</span>
                            Upload Foto KTP
                          </div>
                          <uploadfolder v-model="upload.image" style="height: 200px; width: 200px;">
                            <template #activator>
                              <v-card
                                flat
                                height="200"
                                class="d-flex align-center justify-center"
                                style="border-radius: 15px; border: 2px solid #eaeaea; border-style: dashed;">
                                <div v-if="form.nik_url.length < 1">
                                  <v-icon v-if="!process.image" size="60" color="grey">mdi-cloud-upload-outline</v-icon>
                                  <v-progress-circular v-else indeterminate :color="set_color" />
                                </div>

                                <v-img
                                  v-else
                                  contain
                                  height="200"
                                  :src="cdnFallback['nik_url'] ? getCdnLegacyDisplayUrl(form.nik_url) : getCdnDisplayUrl(form.nik_url)"
                        @error="cdnFallback = { ...cdnFallback, 'nik_url': true }"
                                  :alt="form.nik_url"
                                  class="text-center d-flex align-center">
                                  <div class="d-flex align-center justify-center" style="width: 100%; height: auto;">
                                    <v-icon v-if="!process.image" size="60" color="white">mdi-cloud-upload-outline</v-icon>
                                    <v-progress-circular v-else indeterminate :color="set_color" />
                                  </div>
                                </v-img>
                              </v-card>
                            </template>
                          </uploadfolder>
                        </div>

                        <div class="mt-3">
                          <div class="body-2 mb-2">
                            <span class="red--text">*</span>
                            Upload Proposal / Permohonan
                          </div>
                          <v-card flat height="250">
                            <v-card-title class="pa-0 text-center">
                              <div v-if="form.proposal_url.length < 1 || form.proposal_url.split('.').pop() !== 'pdf'" style="width: 100%;">
                                <uploadImgPdf v-model="upload.proposal" style="height: 250px; width: 100%;">
                                  <template #activator>
                                    <v-card
                                      flat
                                      height="250"
                                      class="d-flex align-center justify-center"
                                      style="border-radius: 15px; border: 2px solid #eaeaea; border-style: dashed; width: 100%;">
                                      <div v-if="form.proposal_url.length < 1">
                                        <v-icon v-if="!process.proposal" size="60" color="grey">mdi-cloud-upload-outline</v-icon>
                                        <v-progress-circular v-else indeterminate :color="set_color" />
                                      </div>

                                      <v-img
                                        v-else
                                        contain
                                        height="250"
                                        :src="cdnFallback['proposal_url'] ? getCdnLegacyDisplayUrl(form.proposal_url) : getCdnDisplayUrl(form.proposal_url)"
                        @error="cdnFallback = { ...cdnFallback, 'proposal_url': true }"
                                        :alt="form.proposal_url"
                                        class="text-center d-flex align-center">
                                        <div class="d-flex align-center justify-center" style="width: 100%; height: auto;">
                                          <v-icon v-if="!process.proposal" size="60" color="white">mdi-cloud-upload-outline</v-icon>
                                          <v-progress-circular v-else indeterminate :color="set_color" />
                                        </div>
                                      </v-img>
                                    </v-card>
                                  </template>
                                </uploadImgPdf>
                              </div>
                              <div v-if="form.proposal_url.length > 0 && form.proposal_url.split('.').pop() === 'pdf'" style="width: 100%;">
                                <iframe :src="cdnFallback['proposal_url'] ? getCdnLegacyDisplayUrl(form.proposal_url) : getCdnDisplayUrl(form.proposal_url)"
                        @error="cdnFallback = { ...cdnFallback, 'proposal_url': true }" height="250" width="100%" frameborder="0"></iframe>
                                <div class="pt-2">
                                  <v-btn elevation="0" dark block @click="form.proposal_url = ''" color="red" rounded>
                                    <v-icon small>mdi-trash-can-outline</v-icon>
                                  </v-btn>
                                </div>
                              </div>
                            </v-card-title>
                          </v-card>
                        </div>
                      </div>
                    </div>
                    </v-col>

                    <v-col cols="12" md="7">
                      <div class="upgrade-pane">
                        <div class="upgrade-pane__header">
                          <div class="upgrade-section-kicker">Konfigurasi Upgrade</div>
                          <div class="subtitle-1 font-weight-bold mt-1">Pilih Fasilitas & Jenis Baru</div>
                          <div class="caption grey--text text--darken-1 mt-1">Tentukan fasilitas umum, jenis pemakaian, dan rincian biaya upgrade.</div>
                        </div>
                      <ValidationProvider name="Pilih Fasilitas Umum" rules="required" v-slot="{ errors, valid }">
                        <div class="body-2 mb-2">
                          <span class="red--text">*</span>
                          Pilih Fasilitas Umum
                        </div>

                        <v-autocomplete
                          v-on:keyup.enter="save()"
                          outlined
                          rounded
                          dense
                          :color="set_color"
                          placeholder="Contoh. Fasilitas Umum"
                          hide-details
                          v-model="selected_product"
                          :items="products"
                          @change="handleProductChange"
                          item-text="name"
                          item-value="id"
                          :error-messages="errors"
                          required
                          style="border-radius: 4px;"
                          class="mb-4">
                          <template v-slot:append-outer>
                            <div v-if="errors.length > 0">
                              <v-tooltip top color="#ff5252">
                                <template v-slot:activator="{ on }">
                                  <v-icon color="error" v-on="on">mdi-alert-circle-outline</v-icon>
                                </template>
                                {{ errors[0] }}
                              </v-tooltip>
                            </div>
                            <div v-if="selected_product && valid">
                              <v-icon color="success">mdi-check-circle-outline</v-icon>
                            </div>
                          </template>
                        </v-autocomplete>
                      </ValidationProvider>

                      <div v-if="selected_product">
                        <div class="upgrade-selection-head pb-4">
                          <div class="form-label mb-1">Pilih Jenis Pemakaian / Penggunaan</div>
                          <div class="caption grey--text text--darken-1">Tidak ada pilihan default. Pilih jenis baru untuk menghitung total upgrade.</div>
                        </div>
                        <v-row>
                          <v-col cols="12" md="12" class="pt-0 mb-1" v-for="(item, i) in event_cat" :key="i">
                            <table class="upgrade-event-table" style="border: 1px solid #ededed; width: 100%; border-collapse: collapse;">
                              <tr>
                                <th colspan="2" style="font-size: 12px; z-index: 1; border: 1px solid #ededed; border-left: none; padding: 8px 12px; text-align: left; background: aliceblue;" class="text--black font-normal">
                                  {{ item.event_cat_name }}
                                </th>
                              </tr>
                              <tr v-for="(event, j) in item.event" :key="j">
                                <td style="font-size: 12px; z-index: 1; border: 1px solid #ededed; border-left: none; padding: 8px 12px; text-align: left; width: 1px; white-space: nowrap;" class="text--black font-normal">
                                  <div class="d-flex justify-space-between">
                                    <div>{{ event.event_name }}</div>
                                    <div>
                                      <v-btn
                                        class="submit-button text-capitalize text--white"
                                        elevation="0"
                                        :color="selected && selected.event_id == event.event_id ? '#09baf7' : '#a20000'"
                                        :loading="loadingHarga && selected && selected.event_id == event.event_id"
                                        @click="selected = event; toCheckHarga(event)"
                                        :disabled="form.start_date === '' || form.end_date === '' || form.start_date === null || form.end_date === null || loadingHarga"
                                        style="font-size: 12px; padding: 0px 4px; margin-top: 4px; color: white;">
                                        {{ selected && selected.event_id == event.event_id ? 'Terpilih' : 'Pilih' }}
                                      </v-btn>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                              <tr v-if="item.event.length == 0">
                                <td colspan="2" style="font-size: 12px; z-index: 1; border: 1px solid #ededed; border-left: none; padding: 8px 12px; text-align: center;" class="text--black">
                                  Belum Ada Jenis Pemakaian / Penggunaan
                                </td>
                              </tr>
                            </table>
                          </v-col>
                        </v-row>
                      </div>

                      <v-divider></v-divider>

                      <div v-if="harga_sewa.length > 0" ref="detailHarga" class="pb-4">
                        <div class="fs-16 mt-5 font-semibold text-gray-900 mb-3">Detail Pemakaian / Penggunaan</div>
                        <div class="upgrade-card upgrade-card--pricing p-3 border border-gray-200">
                          <div class="font-semibold fs-14 text-gray-900 mb-2">
                            Jenis Pemakaian / Penggunaan : {{ selected.event_name }}
                          </div>
                          <v-divider></v-divider>
                          <div>
                            <table style="border: none; width: 100%; border-collapse: collapse;">
                              <tr v-for="(item, index) in harga_sewa" :key="index">
                                <td style="font-size: 12px; z-index: 1; border-left: none; padding: 8px 0px; text-align: left;" class="text--black font-normal">
                                  <div class="d-flex justify-space-between">
                                    <div>{{ item.date | date }}</div>
                                    <div>{{ (item.price*1) | price }}</div>
                                  </div>
                                </td>
                              </tr>
                            </table>
                          </div>
                          <v-divider></v-divider>

                          <div v-if="facilities.length > 0" class="mb-3">
                            <div class="font-semibold fs-14 text-gray-900 mb-2">Fasilitas Tambahan</div>
                            <div class="d-flex flex-column" style="gap: 8px;">
                              <div
                                v-for="facility in facilities"
                                :key="facility.product_facility_id"
                                class="d-flex align-center py-2 px-3"
                                style="border-radius: 6px; transition: background-color 0.15s;"
                                :style="selectedFacilities[facility.product_facility_id] ? 'background-color: #f0f9ff;' : 'background-color: #f9fafb;'">
                                <v-checkbox
                                  :value="!!selectedFacilities[facility.product_facility_id]"
                                  hide-details
                                  color="primary"
                                  dense
                                  class="mr-2"
                                  style="margin-top: 0; flex-shrink: 0;"
                                  @change="(val) => handleFacilityToggle(facility, val)" />
                                <div style="flex: 1; min-width: 0; display: flex; align-items: center; gap: 16px;">
                                  <div style="min-width: 0;">
                                    <div class="fs-14 text-gray-900 font-medium" style="line-height: 1.3;">
                                      {{ facility.product_facility_name }}
                                    </div>
                                    <div class="fs-11 text-gray-500 mt-0.5">
                                      {{ (facility.product_facility_price*1) | price }} <span class="mx-1">•</span> Maks: {{ facility.product_facility_qty }}
                                    </div>
                                  </div>

                                  <div style="flex-shrink: 0;">
                                    <v-select
                                      v-if="selectedFacilities[facility.product_facility_id]"
                                      v-model.number="selectedFacilities[facility.product_facility_id].qty"
                                      :items="getFacilityQtyOptions(facility)"
                                      solo
                                      dense
                                      hide-details
                                      style="width: 90px; border-radius: 6px;"
                                      class="fs-12" />
                                  </div>

                                  <div v-if="selectedFacilities[facility.product_facility_id]" class="text-right" style="min-width: 130px; flex-shrink: 0; margin-left: auto;">
                                    <div class="fs-12 text-gray-900 font-medium">
                                      {{ formatPrice((selectedFacilities[facility.product_facility_id]?.qty || 0) * (parseFloat(facility.product_facility_price) || 0) * totalDays) | price }}
                                    </div>
                                    <div class="fs-10 text-gray-500 mt-0.5">({{ totalDays }} hari)</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <v-divider color="black" class="my-3 border-opacity-100"></v-divider>
                          </div>

                          <div class="d-flex justify-space-between my-3">
                            <div class="font-semibold fs-14 text-gray-900">Total</div>
                            <div class="font-semibold fs-14 text-gray-900">{{ form.total | price }}</div>
                          </div>
                        </div>
                      </div>

                      <div v-else class="pb-5 fs-14">
                        <div class="fs-16 mt-5 font-semibold text-gray-900 mb-3">Detail Pemakaian / Penggunaan</div>
                        <div class="upgrade-empty-state pa-5 text-center border border-gray-200 pb-5" style="border: 1px solid #e0e0e0; border-radius: 8px;">
                          Harga Pemakaian / Penggunaan belum tersedia.
                        </div>
                      </div>
                    </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-form>
          </ValidationObserver>
        </v-card-text>
        <v-divider class="mb-2" style="border-top: 2px dashed #e0e0e0;"/>
        <v-card-actions class="upgrade-dialog__actions">
          <div class="caption grey--text text--darken-1 mr-auto">Pastikan jenis pemakaian baru sudah dipilih sebelum menyimpan upgrade.</div>
          <v-btn text class="text-capitalize px-4" @click="$emit('close')">Tutup</v-btn>
          <v-btn color="primary" class="text-capitalize px-6 elevation-0" :loading="loading" :disabled="saveDisabled" @click="save()">
            Simpan Upgrade
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getCdnDisplayUrl } from '@/utils/cdn'
import { get, post } from "@/service/Axios"
import moment from "moment"
import "moment/locale/id"

export default {
  props: {
    order: {
      type: Object,
      required: true
    },
    dialog: {
      type: Boolean,
      required: true
    },
    products: {
      type: Array,
      required: true
    }
  },
  data () {
    return {
      cdnFallback: {}, // key -> true kalau proxy CDN gagal load, dipakai getCdnLegacyDisplayUrl
      loading: false,
      selected_product: this.order.product_id || '',
      detail_product: {},
      harga_sewa: [],
      facilities: [],
      selectedFacilities: {},
      baseTotalSewa: 0,
      form: {
        name: this.order.customer_name || '',
        email: this.order.customer_email || '',
        mobilephone: this.order.customer_mobilephone || '',
        nik: this.order.customer_nik || '',
        start_date: this.order.event_start_date || '',
        end_date: this.order.event_end_date || '',
        nik_url: this.order.nik_url || '',
        proposal_url: this.order.proposal_url || '',
        permohonan_url: '',
        event_name: this.order.so_event_name || '',
        address: this.order.customer_address || '',
        event_description: this.order.event_description || '',
        product_price_id_array: [],
        total: 0,
        event_cat_id: '',
        event_id: '',
        product_id: this.order.product_id || '',
      },
      upload: {
        image: null,
        proposal: null,
      },
      disabledDates: [],
      menuStart: false,
      menuEnd: false,
      startFormatted: this.order.event_start_date ? moment(this.order.event_start_date).format('DD MMMM YYYY') : '',
      endFormatted: this.order.event_end_date ? moment(this.order.event_end_date).format('DD MMMM YYYY') : '',
      today: moment().format('YYYY-MM-DD'),
      loadingHarga: false,
      holidays: [],
      selected: {
        event_event_cat_id: '',
        event_id: '',
        event_name: '',
        event_product_id: ''
      },
      event_cat: [],
      process: {
        detail: false,
        image: false,
        proposal: false,
        form: false,
      },
      message: {
        error: '',
        success: ''
      },
      dialogSuccess: false,
      successMessage: ''
    }
  },
  components: {
    uploadfolder: () => import(/* webpackPrefetchCategory: true */ '@/components/upload/folder.vue'),
    uploadImgPdf: () => import(/* webpackPrefetchCategory: true */ '@/components/upload/document_pdf.vue'),
  },
  computed: {
    set_color () {
      return this.$store.state.color
    },
    totalDays () {
      if (!this.form.start_date || !this.form.end_date) return 0
      const dateArray = this.getDateRangeExcludingHolidays(
        this.form.start_date,
        this.form.end_date,
        this.holidays
      )
      return dateArray.length
    },
    totalFacilities () {
      let total = 0
      const days = this.totalDays
      Object.values(this.selectedFacilities).forEach(item => {
        if (item && item.qty > 0) {
          const price = parseFloat(item.facility.product_facility_price) || 0
          total += price * item.qty * days
        }
      })
      return total
    },
    oldPriceLines () {
      return Array.isArray(this.order.so_detail) ? this.order.so_detail : []
    },
    oldOrderTotal () {
      return parseFloat(this.order.total) || 0
    },
    newOrderTotal () {
      return parseFloat(this.form.total) || 0
    },
    totalBayarTambahan () {
      return this.newOrderTotal - this.oldOrderTotal
    },
    hasCalculatedNewTotal () {
      return this.harga_sewa.length > 0
    },
    saveDisabled () {
      return this.loading || !this.form.start_date || !this.form.end_date || !this.selected_product || !this.selected.event_id || !this.selected.event_event_cat_id || !this.form.name || !this.form.email || !this.form.mobilephone || !this.form.nik || !this.form.event_name || !this.form.event_description || !this.form.address
    }
  },
  watch: {
    'form.start_date'(val) {
      this.startFormatted = val ? moment(val).format('DD MMMM YYYY') : ''
    },
    'form.end_date'(val) {
      this.endFormatted = val ? moment(val).format('DD MMMM YYYY') : ''
    },
    'upload.image': {
      handler: function () {
        if (this.upload.image) {
          this.process.image = this.upload.image.process
          if (!this.process.image) {
            this.form.nik_url = this.upload.image.fullpath
          }
        }
      },
      deep: true
    },
    'upload.proposal': {
      handler: function () {
        if (this.upload.proposal) {
          this.process.proposal = this.upload.proposal.process
          if (!this.process.proposal) {
            this.form.proposal_url = this.upload.proposal.fullpath
          }
        }
      },
      deep: true
    },
    totalFacilities () {
      this.form.total = this.baseTotalSewa + this.totalFacilities
    },
  },
  mounted () {
    this.fetchDetail()
  },
  methods: {
    getLinePrice (item) {
      return parseFloat(item.price) || parseFloat(item.price_value) || 0
    },
    getFacilitySubtotal (facility) {
      return (parseInt(facility.qty) || 0) * (parseFloat(facility.price) || 0)
    },
    async fetchDetail (id_product) {
      this.process.detail = true
      const params = {
        id: id_product === undefined ? this.order.product_id : id_product,
      }

      await get(`admin/product/detail`, { params })
        .then(response => {
          const res = response.data
          this.process.detail = false

          if (res.status === 200) {
            this.detail_product = res.results.data
            const eventCatRaw = res.results.data.event_cat || []
            const eventRaw = res.results.data.event || []
            const priceRaw = res.results.data.product_price || []
            const facilityRaw = res.results.data.facility || []

            this.facilities = facilityRaw.filter(f => f.product_facility_is_sell === '1')

            const eventProduct = eventRaw.map(cat => {
              const relatePrices = priceRaw.filter(ev => ev.event_id === cat.event_id)
              return {
                ...cat,
                price: relatePrices
              }
            })

            this.event_cat = eventCatRaw.map(cat => {
              const relatedEvents = eventProduct.filter(ev => ev.event_event_cat_id === cat.event_cat_id)
              return {
                ...cat,
                event: relatedEvents
              }
            })

            this.fetchHolliday(this.detail_product.name)
          }
        })
    },
    async fetchHolliday (name_product) {
      const params = {
        year: moment().format('YYYY')
      }

      await get(`sites/agenda/data_sewa`, { params })
        .then(response => {
          const res = response.data
          if (res.status === 200) {
            const allAgenda = res.results.data || []
            const filtered = allAgenda.filter(item => {
              const itemDate = moment(item.date).format('YYYY-MM-DD')
              const startDate = moment(this.form.start_date).format('YYYY-MM-DD')
              return item.location === name_product && itemDate !== startDate
            })
            this.holidays = filtered.map(item => moment(new Date(item.date)).format('YYYY-MM-DD'))
            this.initDisabledDates()
          }
        })
    },
    initDisabledDates () {
      this.disabledDates = this.holidays.map(date => {
        const d = new Date(date)
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      })
    },
    allowedDates (val) {
      const dateStr = moment(val).format('YYYY-MM-DD')
      return !this.disabledDates.includes(dateStr)
    },
    getDateRangeExcludingHolidays (startDate, endDate, holidays) {
      const dates = []
      let current = moment(startDate).startOf('day')
      const end = moment(endDate).startOf('day')

      while (current.isSameOrBefore(end)) {
        const isLibur = holidays.includes(current.format('YYYY-MM-DD'))
        if (!isLibur) {
          dates.push(current.format('YYYY-MM-DD'))
        }
        current.add(1, 'day')
      }

      return dates
    },
    async toCheckHarga (event) {
      this.message.error = ''
      this.loadingHarga = true
      this.harga_sewa = []
      this.baseTotalSewa = 0
      this.form.total = this.totalFacilities
      this.form.product_price_id_array = []

      const date_array = this.getDateRangeExcludingHolidays(
        this.form.start_date,
        this.form.end_date,
        this.holidays
      )

      const params = {
        event_id: event.event_id,
        event_cat_id: event.event_event_cat_id,
        product_id: this.selected_product,
        date_array: JSON.stringify(date_array)
      }

      await get(`sites/order/check_harga`, { params })
        .then(response => {
          const res = response.data
          this.loadingHarga = false

          if (res.status === 200) {
            const result = res.results.data || []
            this.harga_sewa = result
            this.form.product_price_id_array = result

            const totalSewa = result.reduce((acc, item) => {
              return acc + (parseFloat(item.price) || 0)
            }, 0)

            this.baseTotalSewa = totalSewa
            this.form.total = totalSewa + this.totalFacilities
          }
        })
    },
    handleProductChange () {
      this.selected = {
        event_event_cat_id: '',
        event_id: '',
        event_name: '',
        event_product_id: ''
      }
      this.harga_sewa = []
      this.baseTotalSewa = 0
      this.selectedFacilities = {}
      this.form.product_price_id_array = []
      this.form.total = 0
      this.form.product_id = this.selected_product || ''
      this.fetchDetail(this.selected_product)
    },
    handleFacilityToggle (facility, isSelected) {
      if (isSelected) {
        if (!this.selectedFacilities[facility.product_facility_id]) {
          this.$set(this.selectedFacilities, facility.product_facility_id, {
            facility,
            qty: 1
          })
        }
      } else {
        this.$delete(this.selectedFacilities, facility.product_facility_id)
      }
    },
    getFacilityQtyOptions (facility) {
      const maxQty = parseInt(facility.product_facility_qty) || 1
      return Array.from({ length: maxQty }, (_, i) => i + 1)
    },
    formatPrice (price) {
      return price
    },
    closeSuccessDialog () {
      this.dialogSuccess = false
      this.$emit('close')
      this.$emit('saved')
    },
    async save () {
      this.message.error = ''
      const isValid = await this.$refs.form.validate()

      if (!isValid) {
        setTimeout(() => {
          const errors = Object.entries(this.$refs.form.errors)
            .map(([key, value]) => ({ key, value }))
            .filter(error => error.value.length)

          if (errors.length > 0 && this.$refs.form.refs[errors[0].key] && this.$refs.form.refs[errors[0].key].$el) {
            this.$refs.form.refs[errors[0].key].$el.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            })
          }
        }, 10)
        return
      }

      this.loading = true
      this.process.form = true
      this.form.event_id = this.selected ? this.selected.event_id : ''
      this.form.event_cat_id = this.selected ? this.selected.event_event_cat_id : ''
      this.form.product_id = this.selected_product || ''

      const fasilitas_array = Object.values(this.selectedFacilities)
        .filter(item => item && item.qty > 0)
        .map(item => ({
          so_facility_id: item.facility.product_facility_id,
          so_facility_qty: item.qty,
          so_facility_price: parseFloat(item.facility.product_facility_price) || 0
        }))

      const transformedProductPriceIdArray = (this.form.product_price_id_array || []).map(item => {
        if (item && Object.prototype.hasOwnProperty.call(item, 'id') && !Object.prototype.hasOwnProperty.call(item, 'price_id')) {
          const { id, ...rest } = item
          return {
            ...rest,
            price_id: id
          }
        }
        return item
      })

      const formData = {
        ...this.form,
        total: this.totalBayarTambahan,
        product_price_id_array: transformedProductPriceIdArray,
        fasilitas_array,
        upgrade_from_so_id: this.order.id,
      }

      await post(`sites/order/add`, {
        data: formData
      }).then(response => {
        const res = response.data
        this.loading = false
        this.process.form = false

        if (res.status === 200) {
          this.message.success = res.message
          this.successMessage = res.message || 'Upgrade berhasil disimpan.'
          this.dialogSuccess = true
          return
        }

        this.message.error = res.message
      }).catch(() => {
        this.loading = false
        this.process.form = false
      })
    },
  }
}
</script>

<style scoped>
.upgrade-dialog {
  border-radius: 18px;
  overflow: hidden;
}

.upgrade-dialog__title {
  padding: 20px 24px 16px;
  background: linear-gradient(180deg, #ffffff 0%, #fafcff 100%);
}

.upgrade-dialog__body {
  padding: 20px 24px 8px;
}

.upgrade-dialog__actions {
  padding: 16px 24px 20px;
  gap: 8px;
}

.upgrade-section-kicker {
  font-size: 11px;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #5c6bc0;
}

.upgrade-sidebar,
.upgrade-pane {
  height: 100%;
  border: 1px solid #e8edf5;
  border-radius: 16px;
  padding: 18px;
  background: #fff;
}

.upgrade-sidebar {
  background: linear-gradient(180deg, #fafbff 0%, #f6f8fc 100%);
}

.upgrade-sidebar__header,
.upgrade-pane__header {
  margin-bottom: 18px;
}

.upgrade-overview-card {
  padding: 16px;
  border-radius: 16px;
  background: linear-gradient(135deg, #2f3f8f 0%, #5c6bc0 100%);
  color: #fff;
  box-shadow: 0 12px 30px rgba(56, 81, 155, 0.18);
  margin-bottom: 16px;
}

.upgrade-overview-card__label {
  font-size: 11px;
  line-height: 1.4;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.72);
}

.upgrade-overview-card__value {
  margin-top: 8px;
  font-size: 26px;
  line-height: 1.15;
  font-weight: 700;
}

.upgrade-overview-card__subtext {
  margin-top: 8px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.84);
}

.upgrade-summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 16px;
}

.upgrade-summary-cell {
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #e4e9f3;
  background: rgba(255, 255, 255, 0.88);
}

.upgrade-summary-cell--wide {
  grid-column: 1 / -1;
}

.upgrade-summary-label {
  font-size: 11px;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7a8599;
  margin-bottom: 6px;
}

.upgrade-summary-value {
  font-size: 13px;
  line-height: 1.5;
  font-weight: 600;
  color: #1f2937;
}

.upgrade-card {
  border-radius: 14px;
  padding: 14px;
  border: 1px solid #e5eaf2;
}

.upgrade-card--plain {
  background: #fff;
}

.upgrade-card--summary {
  background: linear-gradient(180deg, #f3f8ff 0%, #edf4ff 100%);
  border-color: #cfe0ff;
}

.upgrade-card--pricing {
  border-radius: 14px;
  background: #fff;
}

.upgrade-empty-state {
  background: linear-gradient(180deg, #fcfcfd 0%, #f7f8fa 100%);
}

.upgrade-selection-head {
  border-bottom: 1px dashed #e0e6ef;
}

.upgrade-event-table {
  border-radius: 10px;
  overflow: hidden;
}

.success-dialog {
  border-radius: 18px;
}
</style>
